import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, of, forkJoin } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, tap, share, catchError } from 'rxjs/operators';
import { DataService, CompleteTopicStructureResponse, TopicTreeNode, HierarchyResponse, HierarchyData, HierarchyQuestionData, Card } from './data.service';
import { HierarchyJsonLoaderService, HierarchyJSON, SubjectHierarchy } from './hierarchy-json-loader.service';

// Define hierarchical tree data structure
export interface HierarchicalTreeData {
  subjects: {
    [subjectName: string]: {
      id: string;
      hindiName?: string;
      sections: {
        [sectionName: string]: {
          id: string;
          hindiName?: string;
          topics: {
            [topicName: string]: {
              id: string;
              hindiName?: string;
              subtopics: {
                [subtopicName: string]: {
                  id: string;
                  hindiName?: string;
                  level5?: {
                    [level5Name: string]: {
                      id: string;
                      hindiName?: string;
                      level6?: {
                        [level6Name: string]: {
                          id: string;
                          hindiName?: string;
                          level7?: {
                            [level7Name: string]: {
                              id: string;
                              hindiName?: string;
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  };
  yearwise: {
    exams: {
      [examName: string]: {
        papers: {
          [paperName: string]: {
            years: string[];
          }
        }
      }
    }
  };
}

@Injectable({
  providedIn: 'root'
})
export class TreeService {
  // Central source of truth for tree data
  private treeData$ = new BehaviorSubject<HierarchicalTreeData>({
    subjects: {},
    yearwise: {
      exams: {}
    }
  });

  // Loading state
  private loadingTree$ = new BehaviorSubject<boolean>(false);
  
  // Track if a refresh is currently in progress
  private refreshInProgress = false;

  // Track if we're using JSON-based hierarchies
  private usingJsonHierarchies = false;

  // Cache for loaded hierarchies
  private subjectHierarchiesCache: Map<string, HierarchyJSON[]> = new Map();

  // Cache for hierarchy questions (keyed by topic/subtopic name → Card[])
  private hierarchyQuestionsCache: Map<string, Card[]> = new Map();

  // Track available languages per subject (e.g. 'Geography' → ['english', 'hindi'])
  private subjectLanguages: Map<string, string[]> = new Map();

  // Whether hierarchy API was the data source
  private usingHierarchyApi = false;

  constructor(
    private dataService: DataService,
    private hierarchyJsonLoader: HierarchyJsonLoaderService
  ) {}

  // Public methods for components to interact with the service

  // Get the current tree data as an observable
  getTreeData(): Observable<HierarchicalTreeData> {
    return this.treeData$.asObservable();
  }

  // Get the loading state as an observable
  getLoadingState(): Observable<boolean> {
    return this.loadingTree$.asObservable();
  }

  // Check if we're using JSON-based hierarchies
  isUsingJsonHierarchies(): boolean {
    return this.usingJsonHierarchies;
  }

  // Trigger an immediate refresh of the tree data (no debounce)
  // Tries: 1) New hierarchy API  2) JSON hierarchies  3) Old complete-structure API
  refreshTree(): Promise<void> {
    console.log('🔄 TreeService.refreshTree() - Triggering immediate refresh');
    
    // Check if refresh is already in progress
    if (this.refreshInProgress) {
      console.log('🔄 TreeService - Refresh already in progress, returning existing promise');
      return Promise.resolve();
    }
    
    // Set loading state to true
    this.loadingTree$.next(true);
    this.refreshInProgress = true;
    
    // Return a promise that resolves when the tree data is loaded
    return new Promise<void>((resolve, reject) => {
      // Try the new hierarchy API first
      console.log('🔄 TreeService - Attempting to load from Hierarchy API...');
      
      this.loadFromHierarchyApi()
        .then(loaded => {
          if (loaded) {
            console.log('✅ Successfully loaded from Hierarchy API');
            this.usingHierarchyApi = true;
            this.usingJsonHierarchies = false;
            this.loadingTree$.next(false);
            this.refreshInProgress = false;
            resolve();
            return;
          }

          // Try loading from JSON hierarchies next
          console.log('🔄 TreeService - Hierarchy API not available, trying JSON hierarchies...');
          
          this.loadFromJsonHierarchies()
            .then(jsonLoaded => {
              if (jsonLoaded) {
                console.log('✅ Successfully loaded from JSON hierarchies');
                this.usingJsonHierarchies = true;
                this.loadingTree$.next(false);
                this.refreshInProgress = false;
                resolve();
              } else {
                // Fall back to old API
                console.log('⚠️ JSON hierarchies not available, falling back to old API...');
                this.loadFromApi().then(() => {
                  this.usingJsonHierarchies = false;
                  this.loadingTree$.next(false);
                  this.refreshInProgress = false;
                  resolve();
                }).catch(error => {
                  console.error('❌ All loading methods failed:', error);
                  this.loadingTree$.next(false);
                  this.refreshInProgress = false;
                  reject(error);
                });
              }
            })
            .catch(error => {
              console.error('❌ Error during JSON loading attempt:', error);
              this.loadFromApi().then(() => {
                this.usingJsonHierarchies = false;
                this.loadingTree$.next(false);
                this.refreshInProgress = false;
                resolve();
              }).catch(apiError => {
                console.error('❌ All loading methods failed:', apiError);
                this.loadingTree$.next(false);
                this.refreshInProgress = false;
                reject(apiError);
              });
            });
        })
        .catch(error => {
          console.error('❌ Error during Hierarchy API attempt:', error);
          // Fall through to JSON → API
          this.loadFromJsonHierarchies()
            .then(jsonLoaded => {
              if (jsonLoaded) {
                this.usingJsonHierarchies = true;
                this.loadingTree$.next(false);
                this.refreshInProgress = false;
                resolve();
              } else {
                this.loadFromApi().then(() => {
                  this.loadingTree$.next(false);
                  this.refreshInProgress = false;
                  resolve();
                }).catch(apiError => {
                  this.loadingTree$.next(false);
                  this.refreshInProgress = false;
                  reject(apiError);
                });
              }
            })
            .catch(() => {
              this.loadFromApi().then(() => {
                this.loadingTree$.next(false);
                this.refreshInProgress = false;
                resolve();
              }).catch(apiError => {
                this.loadingTree$.next(false);
                this.refreshInProgress = false;
                reject(apiError);
              });
            });
        });
    });
  }

  /**
   * Check if we loaded from internal hierarchy API (questions are cached)
   */
  isUsingHierarchyApi(): boolean {
    return this.usingHierarchyApi;
  }

  /**
   * Load tree data from the new Hierarchy API (/api/hierarchy/subjects + /api/hierarchy/subject/:subject)
   * Bilingual-aware: merges English + Hindi hierarchies into a single tree using topic_number
   * to match sections/topics across languages.  Hindi names are stored in hindiName fields,
   * Hindi questions in a parallel cache keyed by `topicKey + '__hindi'`.
   * Returns true if successful, false otherwise
   */
  private loadFromHierarchyApi(): Promise<boolean> {
    return new Promise((resolve) => {
      // Step 1: Get all subjects from hierarchy API
      this.dataService.getHierarchySubjects().subscribe(
        subjectsResponse => {
          if (!subjectsResponse || !subjectsResponse.success || !subjectsResponse.subjects || subjectsResponse.subjects.length === 0) {
            console.log('⚠️ No subjects from hierarchy API');
            resolve(false);
            return;
          }

          console.log(`✅ Hierarchy API: Found ${subjectsResponse.subjects.length} subjects`);

          // Step 2: Fetch hierarchy for each subject+language
          const subjectRequests = subjectsResponse.subjects.map(s =>
            this.dataService.getHierarchyBySubject(s.subject, s.language)
          );

          forkJoin(subjectRequests).subscribe(
            (responses: HierarchyResponse[]) => {
              try {
                const hierarchicalData: HierarchicalTreeData = {
                  subjects: {},
                  yearwise: { exams: {} }
                };

                // Clear question caches
                this.hierarchyQuestionsCache.clear();

                // ── Group responses by subject name ──
                const bySubject: Map<string, HierarchyResponse[]> = new Map();
                responses.forEach(resp => {
                  if (!resp || !resp.success || !resp.data) return;
                  const key = resp.data.subject;
                  if (!bySubject.has(key)) bySubject.set(key, []);
                  bySubject.get(key)!.push(resp);
                });

                // ── Process each subject group (bilingual merge) ──
                bySubject.forEach((subjectResponses, subjectName) => {
                  // Sort so English comes first — it provides the tree keys
                  subjectResponses.sort((a, b) => {
                    const la = (a.data.language || 'english').toLowerCase();
                    const lb = (b.data.language || 'english').toLowerCase();
                    if (la === 'english') return -1;
                    if (lb === 'english') return 1;
                    return 0;
                  });

                  // Track which languages are available for this subject
                  const availableLanguages = subjectResponses.map(r => (r.data.language || 'english').toLowerCase());
                  this.subjectLanguages.set(subjectName, availableLanguages);

                  const firstResp = subjectResponses[0];
                  const subjectId = firstResp.data.subject_topic_id || subjectName;

                  // Initialise subject node
                  hierarchicalData.subjects[subjectName] = {
                    id: subjectId,
                    hindiName: '', // Will be updated from complete-structure API
                    sections: {}
                  };
                  const subjectData = hierarchicalData.subjects[subjectName];

                  // Maps to look up English tree keys by topic_id
                  // topic_id is the same across English and Hindi responses
                  const sectionTopicIdToKey: Map<string, string> = new Map();
                  const topicIdToKey: Map<string, string> = new Map();
                  const subtopicIdToKey: Map<string, string> = new Map();

                  subjectResponses.forEach(resp => {
                    const hData = resp.data;
                    const language = (hData.language || 'english').toLowerCase();
                    const isEnglish = language === 'english';
                    const isHindi = language === 'hindi';

                    console.log(`📚 Processing ${subjectName} [${language}] — ${hData.total_questions}q, ${hData.total_sections}s, ${hData.total_topics}t`);

                    // ── Sections ──
                    hData.sections.forEach((section, sIdx) => {
                      let sectionKey: string;

                      if (isEnglish) {
                        // Use name_eng from response if available, fall back to name
                        sectionKey = section.name_eng || section.name;
                        if (section.topic_id) sectionTopicIdToKey.set(section.topic_id, sectionKey);
                        subjectData.sections[sectionKey] = {
                          id: section.topic_id || `hierarchy_section_${section.numeral || section.name}`,
                          hindiName: section.name_hindi || section.title || section.name,
                          topics: {}
                        };
                      } else {
                        // Hindi: match by topic_id first, then fall back to name
                        sectionKey = (section.topic_id && sectionTopicIdToKey.has(section.topic_id))
                          ? sectionTopicIdToKey.get(section.topic_id)!
                          : (section.name_eng || section.name);
                        if (subjectData.sections[sectionKey]) {
                          // Update hindiName on the existing English section node
                          subjectData.sections[sectionKey].hindiName = section.name_hindi || section.name;
                        } else {
                          subjectData.sections[sectionKey] = {
                            id: section.topic_id || `hierarchy_section_${section.numeral || section.name}`,
                            hindiName: section.name_hindi || section.name,
                            topics: {}
                          };
                        }
                        if (isHindi && !subjectData.hindiName) {
                          // Don't overwrite with English name; leave for complete-structure to fill
                        }
                      }

                      const sectionData = subjectData.sections[sectionKey];

                      // Cache direct section questions (language-tagged)
                      if (section.direct_questions && section.direct_questions.length > 0) {
                        const cacheKey = isHindi ? `${sectionKey}__hindi` : sectionKey;
                        this.cacheHierarchyQuestions(cacheKey, section.direct_questions, language);
                      }

                      // ── Topics ──
                      section.topics.forEach(topic => {
                        let topicKey: string;

                        if (isEnglish) {
                          topicKey = topic.name_eng || topic.name;
                          if (topic.topic_id) topicIdToKey.set(topic.topic_id, topicKey);
                          sectionData.topics[topicKey] = {
                            id: topic.topic_id || `hierarchy_topic_${topic.marker || topic.name}`,
                            hindiName: topic.name_hindi || topic.name,
                            subtopics: {}
                          };
                        } else {
                          // Hindi: match by topic_id first, fall back to name_eng or name
                          topicKey = (topic.topic_id && topicIdToKey.has(topic.topic_id))
                            ? topicIdToKey.get(topic.topic_id)!
                            : (topic.name_eng || topic.name);
                          if (sectionData.topics[topicKey]) {
                            sectionData.topics[topicKey].hindiName = topic.name_hindi || topic.name;
                          } else {
                            sectionData.topics[topicKey] = {
                              id: topic.topic_id || `hierarchy_topic_${topic.marker || topic.name}`,
                              hindiName: topic.name_hindi || topic.name,
                              subtopics: {}
                            };
                          }
                        }

                        const topicData = sectionData.topics[topicKey];

                        // Cache topic questions (language-tagged)
                        if (topic.questions && topic.questions.length > 0) {
                          const cacheKey = isHindi ? `${topicKey}__hindi` : topicKey;
                          this.cacheHierarchyQuestions(cacheKey, topic.questions, language);
                        }

                        // ── Subtopics ──
                        if (topic.subtopics && topic.subtopics.length > 0) {
                          topic.subtopics.forEach((subtopic, stIdx) => {
                            let subtopicKey: string;

                            if (isEnglish) {
                              subtopicKey = subtopic.name_eng || subtopic.name;
                              if (subtopic.topic_id) subtopicIdToKey.set(subtopic.topic_id, subtopicKey);
                            } else {
                              // Hindi: match by topic_id first, fall back to positional match
                              subtopicKey = (subtopic.topic_id && subtopicIdToKey.has(subtopic.topic_id))
                                ? subtopicIdToKey.get(subtopic.topic_id)!
                                : (() => {
                                    const existingKeys = Object.keys(topicData.subtopics);
                                    return existingKeys[stIdx] || (subtopic.name_eng || subtopic.name);
                                  })();
                              if (topicData.subtopics[subtopicKey]) {
                                topicData.subtopics[subtopicKey].hindiName = subtopic.name_hindi || subtopic.name;
                              }
                            }

                            if (!topicData.subtopics[subtopicKey]) {
                              topicData.subtopics[subtopicKey] = {
                                id: subtopic.topic_id || `hierarchy_subtopic_${subtopic.marker || subtopic.name}`,
                                hindiName: subtopic.name_hindi || subtopic.name
                              };
                            }

                            if (subtopic.questions && subtopic.questions.length > 0) {
                              const cacheKey = isHindi ? `${subtopicKey}__hindi` : subtopicKey;
                              this.cacheHierarchyQuestions(cacheKey, subtopic.questions, language);
                            }

                            // Handle deeper subtopics (level 5+)
                            if (subtopic.subtopics && subtopic.subtopics.length > 0) {
                              const parentSubtopicData = topicData.subtopics[subtopicKey];
                              if (!parentSubtopicData.level5) parentSubtopicData.level5 = {};
                              subtopic.subtopics.forEach((deepSub: any) => {
                                const deepKey = isEnglish
                                  ? (deepSub.name_eng || deepSub.name)
                                  : (deepSub.topic_id && subtopicIdToKey.has(deepSub.topic_id)
                                      ? subtopicIdToKey.get(deepSub.topic_id)!
                                      : (deepSub.name_eng || deepSub.name));
                                if (isEnglish && deepSub.topic_id) {
                                  subtopicIdToKey.set(deepSub.topic_id, deepKey);
                                }
                                if (!parentSubtopicData.level5![deepKey]) {
                                  parentSubtopicData.level5![deepKey] = {
                                    id: deepSub.topic_id || `hierarchy_deep_${deepSub.marker || deepSub.name}`,
                                    hindiName: deepSub.name_hindi || deepSub.name
                                  };
                                } else if (isHindi) {
                                  parentSubtopicData.level5![deepKey].hindiName = deepSub.name_hindi || deepSub.name;
                                }
                                if (deepSub.questions && deepSub.questions.length > 0) {
                                  const cacheKey = isHindi ? `${deepKey}__hindi` : deepKey;
                                  this.cacheHierarchyQuestions(cacheKey, deepSub.questions, language);
                                }
                              });
                            }
                          });
                        }
                      });

                      // Orphan subtopics at section level
                      if (section.orphan_subtopics) {
                        section.orphan_subtopics.forEach(subtopic => {
                          const orphanKey = subtopic.name_eng || subtopic.name;
                          if (!sectionData.topics[orphanKey]) {
                            sectionData.topics[orphanKey] = {
                              id: subtopic.topic_id || `hierarchy_orphan_subtopic_${subtopic.marker || subtopic.name}`,
                              hindiName: subtopic.name_hindi || subtopic.name,
                              subtopics: {}
                            };
                          }
                          if (subtopic.questions && subtopic.questions.length > 0) {
                            const cacheKey = isHindi ? `${orphanKey}__hindi` : orphanKey;
                            this.cacheHierarchyQuestions(cacheKey, subtopic.questions, language);
                          }
                        });
                      }
                    });

                    // ── Orphan topics (no section) ──
                    if (hData.orphan_topics) {
                      hData.orphan_topics.forEach(topic => {
                        const orphanSectionKey = topic.name_eng || topic.name;
                        if (!subjectData.sections[orphanSectionKey]) {
                          subjectData.sections[orphanSectionKey] = {
                            id: topic.topic_id || `hierarchy_orphan_${topic.marker || topic.name}`,
                            hindiName: topic.name_hindi || topic.name,
                            topics: {}
                          };
                        } else if (isHindi) {
                          subjectData.sections[orphanSectionKey].hindiName = topic.name_hindi || topic.name;
                        }
                        if (topic.questions && topic.questions.length > 0) {
                          const cacheKey = isHindi ? `${orphanSectionKey}__hindi` : orphanSectionKey;
                          this.cacheHierarchyQuestions(cacheKey, topic.questions, language);
                        }
                        if (topic.subtopics) {
                          topic.subtopics.forEach(subtopic => {
                            const stKey = subtopic.name_eng || subtopic.name;
                            if (!subjectData.sections[orphanSectionKey].topics[stKey]) {
                              subjectData.sections[orphanSectionKey].topics[stKey] = {
                                id: subtopic.topic_id || `hierarchy_orphan_subtopic_${subtopic.marker || subtopic.name}`,
                                hindiName: subtopic.name_hindi || subtopic.name,
                                subtopics: {}
                              };
                            } else if (isHindi) {
                              subjectData.sections[orphanSectionKey].topics[stKey].hindiName = subtopic.name_hindi || subtopic.name;
                            }
                            if (subtopic.questions && subtopic.questions.length > 0) {
                              const cacheKey = isHindi ? `${stKey}__hindi` : stKey;
                              this.cacheHierarchyQuestions(cacheKey, subtopic.questions, language);
                            }
                          });
                        }
                      });
                    }
                  }); // end subjectResponses.forEach
                }); // end bySubject.forEach

                // Ensure the UI includes all subjects even when hierarchy API has partial coverage.
                this.mergeMissingSubjectsFromCompleteStructure(hierarchicalData)
                  .then(mergedData => {
                    this.treeData$.next(mergedData);
                    console.log(`✅ Hierarchy API: Tree built with ${Object.keys(mergedData.subjects).length} subjects, ${this.hierarchyQuestionsCache.size} question caches`);
                    resolve(true);
                  })
                  .catch(error => {
                    console.warn('⚠️ Failed to merge missing subjects from complete structure API, using hierarchy-only tree:', error);
                    this.treeData$.next(hierarchicalData);
                    console.log(`✅ Hierarchy API: Tree built with ${Object.keys(hierarchicalData.subjects).length} subjects, ${this.hierarchyQuestionsCache.size} question caches`);
                    resolve(true);
                  });
              } catch (error) {
                console.error('❌ Error processing hierarchy API response:', error);
                resolve(false);
              }
            },
            error => {
              console.error('❌ Error fetching hierarchy data:', error);
              resolve(false);
            }
          );
        },
        error => {
          console.error('❌ Error fetching hierarchy subjects:', error);
          resolve(false);
        }
      );
    });
  }

  /**
   * Merge subjects missing from hierarchy API using complete topic structure API.
   * This avoids showing only a partial subject set when hierarchy data exists for
   * only recently processed batches.
   */
  private mergeMissingSubjectsFromCompleteStructure(
    baseTreeData: HierarchicalTreeData
  ): Promise<HierarchicalTreeData> {
    return new Promise((resolve) => {
      this.dataService.getCompleteTopicStructure().subscribe(
        (response: CompleteTopicStructureResponse) => {
          if (!response || !response.success || !Array.isArray(response.topics) || !response.trees) {
            resolve(baseTreeData);
            return;
          }

          let mergedSubjects = 0;

          response.topics.forEach(subject => {
            const subjectName = subject.name_eng;
            if (baseTreeData.subjects[subjectName]) {
              // Update Hindi name from complete-structure API
              if (subject.name_hindi) {
                baseTreeData.subjects[subjectName].hindiName = subject.name_hindi;
              }
              return;
            }

            const subjectData: any = {
              id: subject.topic_id,
              hindiName: subject.name_hindi,
              sections: {}
            };

            const subjectTree = response.trees[subject.topic_id];
            if (subjectTree) {
              this.processSubjectTree(subjectTree, subjectData);
            }

            baseTreeData.subjects[subjectName] = subjectData;
            mergedSubjects++;
          });

          if (mergedSubjects > 0) {
            console.log(`🔀 Added ${mergedSubjects} subjects from complete structure API`);
          }

          resolve(baseTreeData);
        },
        error => {
          console.warn('⚠️ Unable to load complete structure for subject merge:', error);
          resolve(baseTreeData);
        }
      );
    });
  }

  /**
   * Get available languages for a subject (e.g. ['english', 'hindi'])
   */
  getSubjectLanguages(subjectName: string): string[] {
    return this.subjectLanguages.get(subjectName) || ['english'];
  }

  /**
   * Get cached questions for a topic/subtopic name from the hierarchy data.
   * When language is 'bilingual', merges English and Hindi cards by question_number.
   * When language is 'hindi', returns Hindi-only cards.
   * Otherwise returns English-only cards.
   */
  getHierarchyQuestions(topicName: string, language: string = 'english'): Card[] | null {
    const langLower = language.toLowerCase();

    if (langLower === 'hindionly' || langLower === 'hindi') {
      // Return Hindi-only questions
      const hindiCards = this.hierarchyQuestionsCache.get(`${topicName}__hindi`);
      if (hindiCards && hindiCards.length > 0) {
        console.log(`📚 Found ${hindiCards.length} Hindi cached questions for: ${topicName}`);
        return hindiCards;
      }
      return null;
    }

    if (langLower === 'bilingual') {
      // Merge English + Hindi cards by question_number
      const englishCards = this.hierarchyQuestionsCache.get(topicName);
      const hindiCards = this.hierarchyQuestionsCache.get(`${topicName}__hindi`);

      if (!englishCards || englishCards.length === 0) {
        return hindiCards && hindiCards.length > 0 ? hindiCards : null;
      }

      // Build a lookup of Hindi cards by question_number
      const hindiByQNum: Map<number, Card> = new Map();
      if (hindiCards) {
        hindiCards.forEach(hc => {
          if (hc.questionNumber != null) hindiByQNum.set(hc.questionNumber, hc);
        });
      }

      // Merge Hindi fields onto English cards
      const merged = englishCards.map(engCard => {
        const hCard = engCard.questionNumber != null ? hindiByQNum.get(engCard.questionNumber) : undefined;
        if (hCard) {
          return {
            ...engCard,
            hindiQuestionText: hCard.questionText || hCard.hindiQuestionText || '',
            hindiImageUrl: hCard.imageUrl || hCard.hindiImageUrl || '',
            hindiQuestionImageUrl: hCard.imageUrl || hCard.hindiQuestionImageUrl || '',
            hindiAnswerText: hCard.answerText || hCard.hindiAnswerText || '',
            hindiAnswerImageUrl: hCard.answerImageUrl || hCard.hindiAnswerImageUrl || '',
            hindiExplanationText: hCard.explanationText || hCard.hindiExplanationText || '',
            hindiExplanationImageUrl: hCard.explanationImageUrl || hCard.hindiExplanationImageUrl || '',
            hindiOptionAImageUrl: hCard.optionAImageUrl || hCard.hindiOptionAImageUrl || '',
            hindiOptionBImageUrl: hCard.optionBImageUrl || hCard.hindiOptionBImageUrl || '',
            hindiOptionCImageUrl: hCard.optionCImageUrl || hCard.hindiOptionCImageUrl || '',
            hindiOptionDImageUrl: hCard.optionDImageUrl || hCard.hindiOptionDImageUrl || '',
          };
        }
        return engCard;
      });

      console.log(`📚 Merged bilingual: ${merged.length} questions (${hindiByQNum.size} Hindi matches) for: ${topicName}`);
      return merged.length > 0 ? merged : null;
    }

    // Default: English only
    const questions = this.hierarchyQuestionsCache.get(topicName);
    if (questions && questions.length > 0) {
      console.log(`📚 Found ${questions.length} cached questions for: ${topicName}`);
      return questions;
    }
    return null;
  }

  /**
   * Cache questions from hierarchy data as Card[] for quick lookup by topic name.
   * Hindi questions are cached under `topicName + '__hindi'` so they can be
   * merged with English when bilingual mode is selected.
   */
  private cacheHierarchyQuestions(cacheKey: string, questions: HierarchyQuestionData[], language: string): void {
    const cards = questions.map(q => this.dataService.convertHierarchyQuestionToCard(q, language));
    const existing = this.hierarchyQuestionsCache.get(cacheKey) || [];
    this.hierarchyQuestionsCache.set(cacheKey, [...existing, ...cards]);
  }

  /**
   * Load tree data from JSON hierarchies
   * Returns true if successful, false otherwise
   */
  private loadFromJsonHierarchies(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.hierarchyJsonLoader.getAllSubjectHierarchies().subscribe(
        (allSubjects: SubjectHierarchy[]) => {
          if (!allSubjects || allSubjects.length === 0) {
            console.log('⚠️ No JSON hierarchies available');
            resolve(false);
            return;
          }

          console.log(`✅ Found ${allSubjects.length} subjects from JSON hierarchies`);

          try {
            const hierarchicalData: HierarchicalTreeData = {
              subjects: {},
              yearwise: {
                exams: {}
              }
            };

            // Process each subject
            allSubjects.forEach(subjectData => {
              console.log(`📚 Processing subject: ${subjectData.subjectName}`);
              
              // Initialize subject in hierarchical data
              hierarchicalData.subjects[subjectData.subjectName] = {
                id: subjectData.subjectId,
                hindiName: subjectData.subjectName,
                sections: {}
              };

              // Cache the hierarchies for this subject
              this.subjectHierarchiesCache.set(subjectData.subjectName, subjectData.hierarchies);

              // Process all hierarchies for this subject and merge them
              subjectData.hierarchies.forEach((hierarchy, batchIndex) => {
                this.processHierarchyJson(
                  hierarchy,
                  hierarchicalData.subjects[subjectData.subjectName],
                  batchIndex
                );
              });
            });

            // Update the tree data
            this.treeData$.next(hierarchicalData);
            console.log('✅ Successfully loaded and processed JSON hierarchies');
            resolve(true);
          } catch (error) {
            console.error('❌ Error processing JSON hierarchies:', error);
            reject(error);
          }
        },
        error => {
          console.error('❌ Error fetching JSON hierarchies:', error);
          resolve(false); // Return false instead of rejecting, so we can fall back to API
        }
      );
    });
  }

  /**
   * Process a single hierarchy.json file and add its data to the subject
   */
  private processHierarchyJson(
    hierarchy: HierarchyJSON,
    subjectData: any,
    batchIndex: number
  ): void {
    const batchId = `batch_${batchIndex}`;

    // Process sections
    hierarchy.sections.forEach(section => {
      const sectionKey = section.name;
      
      if (!subjectData.sections[sectionKey]) {
        subjectData.sections[sectionKey] = {
          id: `${batchId}_section_${section.numeral}`,
          hindiName: section.title,
          topics: {}
        };
      }

      const sectionData = subjectData.sections[sectionKey];

      // Process topics within section
      section.topics.forEach(topic => {
        const topicKey = topic.name;
        
        if (!sectionData.topics[topicKey]) {
          sectionData.topics[topicKey] = {
            id: `${batchId}_topic_${topic.marker}`,
            hindiName: topic.name,
            subtopics: {}
          };
        }

        const topicData = sectionData.topics[topicKey];

        // Process subtopics
        if (topic.subtopics && Array.isArray(topic.subtopics)) {
          topic.subtopics.forEach(subtopic => {
            const subtopicKey = subtopic.name;
            
            if (!topicData.subtopics[subtopicKey]) {
              topicData.subtopics[subtopicKey] = {
                id: `${batchId}_subtopic_${subtopic.marker}`,
                hindiName: subtopic.name,
                questions: this.convertQuestions(subtopic.questions, batchId)
              };
            }
          });
        }

        // Add direct topic questions
        if (topic.questions && Array.isArray(topic.questions)) {
          if (!topicData.directQuestions) {
            topicData.directQuestions = [];
          }
          topicData.directQuestions.push(...this.convertQuestions(topic.questions, batchId));
        }
      });

      // Process direct section questions
      if (section.direct_questions && Array.isArray(section.direct_questions)) {
        if (!sectionData.directQuestions) {
          sectionData.directQuestions = [];
        }
        sectionData.directQuestions.push(...this.convertQuestions(section.direct_questions, batchId));
      }
    });

    // Process orphan topics
    hierarchy.orphan_topics.forEach(topic => {
      const orphanKey = `orphan_${topic.name}`;
      
      if (!subjectData.sections[orphanKey]) {
        subjectData.sections[orphanKey] = {
          id: `${batchId}_orphan_topic_${topic.marker}`,
          hindiName: topic.name,
          topics: {},
          isOrphan: true
        };
      }

      const orphanSection = subjectData.sections[orphanKey];

      // Add orphan topic data as if it were a topic
      const topicKey = topic.name;
      if (!orphanSection.topics[topicKey]) {
        orphanSection.topics[topicKey] = {
          id: `${batchId}_orphan_topic_${topic.marker}`,
          hindiName: topic.name,
          subtopics: {}
        };
      }

      const topicData = orphanSection.topics[topicKey];

      // Process subtopics under orphan topic
      if (topic.subtopics && Array.isArray(topic.subtopics)) {
        topic.subtopics.forEach(subtopic => {
          const subtopicKey = subtopic.name;
          
          if (!topicData.subtopics[subtopicKey]) {
            topicData.subtopics[subtopicKey] = {
              id: `${batchId}_orphan_subtopic_${subtopic.marker}`,
              hindiName: subtopic.name,
              questions: this.convertQuestions(subtopic.questions, batchId)
            };
          }
        });
      }

      // Add orphan topic direct questions
      if (topic.questions && Array.isArray(topic.questions)) {
        if (!topicData.directQuestions) {
          topicData.directQuestions = [];
        }
        topicData.directQuestions.push(...this.convertQuestions(topic.questions, batchId));
      }
    });
  }

  /**
   * Convert question data from hierarchy format
   */
  private convertQuestions(questions: any[], batchId: string): any[] {
    if (!Array.isArray(questions)) return [];
    
    return questions.map(q => ({
      id: `${batchId}_question_${q.question_number}`,
      question_number: q.question_number,
      global_number: q.global_number,
      page: q.page,
      topic_id: `${batchId}_${q.question_number}`
    }));
  }

  /**
   * Load tree data from API (fallback)
   */
  private loadFromApi(): Promise<void> {
    return new Promise((resolve, reject) => {
      console.log('🔄 TreeService - Loading from API...');
      
      this.loadCompleteTreeStructure().subscribe(
        treeData => {
          console.log('✅ API call successful, processing tree data');
          this.processTreeData(treeData);
          console.log('✅ Tree loaded from API');
          resolve();
        },
        error => {
          console.error('❌ Error loading tree from API:', error);
          reject(error);
        }
      );
    });
  }

  // Initialize the tree service and load initial data
  initialize(): void {
    if (Object.keys(this.treeData$.value.subjects).length === 0) {
      this.refreshTree();
    }
  }

  // Private methods

  // Load complete tree structure from the data service
  private loadCompleteTreeStructure(): Observable<CompleteTopicStructureResponse> {
    console.log('CALLING API: /topics/complete-structure');
    return this.dataService.getCompleteTopicStructure();
  }

  // Process tree data from API into hierarchical structure
  private processTreeData(response: CompleteTopicStructureResponse): void {
    if (!response || !response.success) {
      console.error('Invalid or unsuccessful tree structure response');
      return;
    }

    const hierarchicalData: HierarchicalTreeData = {
      subjects: {},
      yearwise: {
        exams: {}
      }
    };

    // Process each root topic (subject)
    response.topics.forEach(subject => {
      hierarchicalData.subjects[subject.name_eng] = {
        id: subject.topic_id,
        hindiName: subject.name_hindi,
        sections: {}
      };

      // Process the complete tree for this subject
      if (response.trees[subject.topic_id]) {
        this.processSubjectTree(
          response.trees[subject.topic_id],
          hierarchicalData.subjects[subject.name_eng]
        );
      } else {
        console.warn(`No tree found for subject ID: ${subject.topic_id}`);
      }
    });

    // Update the BehaviorSubject with new data
    this.treeData$.next(hierarchicalData);
  }

  // Process a subject tree and its children
  private processSubjectTree(tree: TopicTreeNode, subjectData: any): void {
    if (!tree.children || tree.children.length === 0) {
      console.warn('Subject tree has no children, skipping');
      return;
    }

    // Process each child (sections)
    tree.children.forEach(section => {
      subjectData.sections[section.name_eng] = {
        id: section.topic_id,
        hindiName: section.name_hindi,
        topics: {}
      };

      // Process topics under this section
      if (section.children && section.children.length > 0) {
        section.children.forEach(topic => {
          subjectData.sections[section.name_eng].topics[topic.name_eng] = {
            id: topic.topic_id,
            hindiName: topic.name_hindi,
            subtopics: {}
          };

          // Process subtopics (level 4)
          if (topic.children && topic.children.length > 0) {
            topic.children.forEach(subtopic => {
              subjectData.sections[section.name_eng].topics[topic.name_eng].subtopics[subtopic.name_eng] = {
                id: subtopic.topic_id,
                hindiName: subtopic.name_hindi,
                level5: {}
              };
              
              // Process level 5 items
              if (subtopic.children && subtopic.children.length > 0) {
                subtopic.children.forEach(level5Item => {
                  if (!subjectData.sections[section.name_eng].topics[topic.name_eng].subtopics[subtopic.name_eng].level5) {
                    subjectData.sections[section.name_eng].topics[topic.name_eng].subtopics[subtopic.name_eng].level5 = {};
                  }
                  
                  subjectData.sections[section.name_eng].topics[topic.name_eng].subtopics[subtopic.name_eng].level5[level5Item.name_eng] = {
                    id: level5Item.topic_id,
                    hindiName: level5Item.name_hindi,
                    level6: {}
                  };
                  
                  // Process level 6 items
                  if (level5Item.children && level5Item.children.length > 0) {
                    level5Item.children.forEach(level6Item => {
                      if (!subjectData.sections[section.name_eng].topics[topic.name_eng].subtopics[subtopic.name_eng].level5[level5Item.name_eng].level6) {
                        subjectData.sections[section.name_eng].topics[topic.name_eng].subtopics[subtopic.name_eng].level5[level5Item.name_eng].level6 = {};
                      }
                      
                      subjectData.sections[section.name_eng].topics[topic.name_eng].subtopics[subtopic.name_eng].level5[level5Item.name_eng].level6[level6Item.name_eng] = {
                        id: level6Item.topic_id,
                        hindiName: level6Item.name_hindi,
                        level7: {}
                      };
                      
                      // Process level 7 items
                      if (level6Item.children && level6Item.children.length > 0) {
                        level6Item.children.forEach(level7Item => {
                          if (!subjectData.sections[section.name_eng].topics[topic.name_eng].subtopics[subtopic.name_eng].level5[level5Item.name_eng].level6[level6Item.name_eng].level7) {
                            subjectData.sections[section.name_eng].topics[topic.name_eng].subtopics[subtopic.name_eng].level5[level5Item.name_eng].level6[level6Item.name_eng].level7 = {};
                          }
                          
                          subjectData.sections[section.name_eng].topics[topic.name_eng].subtopics[subtopic.name_eng].level5[level5Item.name_eng].level6[level6Item.name_eng].level7[level7Item.name_eng] = {
                            id: level7Item.topic_id,
                            hindiName: level7Item.name_hindi
                          };
                        });
                      }
                    });
                  }
                });
              }
            });
          }
        });
      }
    });
  }
}
